import { useMutation } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import {
  AgentThreadRecord,
  ClientToolSpec,
  ClientViewingContext,
} from "../AgentThreadRecord";

interface StartAgentThreadParams {
  /** User text to seed the conversation with. Optional when ``invokeSkills`` is set. */
  message?: string;
  /** Skills to fabricate into the new session, in order. Pass a single-element
   * array for the common "invoke one skill" case. Optional when ``message`` is set. */
  invokeSkills?: { skill_id: string; version?: number | null }[];
  orgId: string;
  clientContext?: ClientViewingContext;
  modelProfile?: string;
  clientTools?: ClientToolSpec[];
}

/**
 * Single mutation for "start a new thread" — typed message, skill-seeded, or any
 * combination. Mirrors the backend StartAgentSessionRequest's optional-trigger
 * model: at least one of ``message`` / ``invokeSkills`` (or ``goals``,
 * SDK-only) must be present and the server enforces that.
 *
 * @example
 * ```tsx
 * const startAgentThread = useStartAgentThread();
 * await startAgentThread.mutateAsync({ message: "Hello", orgId, context });
 * await startAgentThread.mutateAsync({ invokeSkills: [{ skill_id, version }], orgId, context });
 * ```
 */
export function useStartAgentThread() {
  const { agentThreadService } = useDomainServices();

  return useMutation<AgentThreadRecord, Error, StartAgentThreadParams>({
    mutationFn: async ({
      message,
      invokeSkills,
      orgId,
      clientContext,
      modelProfile,
      clientTools,
    }) => {
      return await agentThreadService.startAgentThread(message, {
        orgId,
        invokeSkills,
        clientContext,
        modelProfile,
        clientTools,
      });
    },
  });
}
